import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const CRITICIDAD_CONFIG = {
  critica: { label: '🔴 Crítica',  bg: 'var(--red-dim)', color: 'var(--red)', border: '#F5B7B1' },
  alta:    { label: '🟠 Alta',     bg: 'var(--amber-dim)', color: '#D45D00', border: '#FAD7A0' },
  media:   { label: '🟡 Media',    bg: '#FFFDE7', color: '#856404', border: '#FAEBCC' },
  baja:    { label: '🟢 Baja',     bg: 'var(--green-dim)', color: 'var(--green)', border: '#A9DFBF' },
  null:    { label: '⬜ Sin definir', bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
};

function CriticidadBadge({ value }) {
  const cfg = CRITICIDAD_CONFIG[value] || CRITICIDAD_CONFIG['null'];
  return (
    <span style={{
      color: cfg.color,
      fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

export default function MatrizCriticidad() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API_URL}/assets/matriz`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(setAssets)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const locations = [...new Set(assets.map(a => a.location_name).filter(Boolean))].sort();

  const filtered = assets.filter(a => {
    if (filterLocation && (a.location_name || '') !== filterLocation) return false;
    if (!filter) return true;
    return (
      (a.hostname || '').toLowerCase().includes(filter.toLowerCase()) ||
      (a.user_name || '').toLowerCase().includes(filter.toLowerCase()) ||
      (a.location_name || '').toLowerCase().includes(filter.toLowerCase())
    );
  });

  // Agrupar por criticidad para el resumen (respeta los filtros activos)
  const counts = filtered.reduce((acc, a) => {
    const k = a.criticidad || 'null';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Matriz de Criticidad</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Activos clasificados por nivel de impacto en el negocio
        </p>
      </div>

      {/* Resumen por nivel */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {['critica', 'alta', 'media', 'baja', 'null'].map(k => {
          const cfg = CRITICIDAD_CONFIG[k];
          return (
            <div key={k} style={{
              background: cfg.bg, border: `1px solid ${cfg.border}`,
              borderRadius: 10, padding: '10px 18px', minWidth: 120, textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: cfg.color }}>{counts[k] || 0}</div>
              <div style={{ fontSize: 12, color: cfg.color, fontWeight: 500 }}>{cfg.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select
          value={filterLocation}
          onChange={e => setFilterLocation(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, minWidth: 200, background: 'var(--surface)' }}
        >
          <option value="">Todas las localidades</option>
          {locations.map(loc => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filtrar por hostname o usuario..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, width: 280 }}
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="spinner" style={{ margin: '40px auto' }} />
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>Activo</th>
                  <th style={th}>Usuario</th>
                  <th style={th}>Ubicación</th>
                  <th style={th}>Tipo</th>
                  <th style={th}>Ambiente</th>
                  <th style={th}>Criticidad</th>
                  <th style={th}>Disponibilidad</th>
                  <th style={th}>RTO (hs)</th>
                  <th style={th}>DRP</th>
                  <th style={th}>Alta Disp.</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)' }}>Sin resultados</td></tr>
                )}
                {filtered.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                    <td style={td}>
                      <span className="mono" style={{ fontWeight: 600, color: 'var(--ink)' }}>{a.hostname}</span>
                    </td>
                    <td style={td}>{a.user_name || <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={td}>{a.location_name || <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={td}>{a.device_subtype || <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={td}>{a.ambiente || <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={td}><CriticidadBadge value={a.criticidad} /></td>
                    <td style={td}>{a.disponibilidad_servicio || <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{a.rto_horas != null ? a.rto_horas : <span style={{ color: 'var(--ink-3)' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {a.drp ? <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span> : <span style={{ color: 'var(--border-2)' }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {a.alta_disponibilidad ? <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span> : <span style={{ color: 'var(--border-2)' }}>—</span>}
                    </td>
                    <td style={td}>
                      <Link to={`/assets/${a.id}`} className="btn btn-secondary btn-sm">Ver</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const th = {
  padding: '10px 14px', textAlign: 'left', fontSize: 11,
  fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};
const td = { padding: '10px 14px', color: 'var(--ink-2)', verticalAlign: 'middle' };
